import {
  SUPERNOVA_COLLECTION_ADDRESS,
  SUPERNOVA_TOKEN_IDS,
} from "./supernovaContracts";
import { lockDaysFromSeconds, lotIdFor } from "./supernovaStakeRead";

/**
 * supernovaClosedLots — the member's CLOSED stake lots, rebuilt from the staking
 * contract's own event log.
 *
 * This is the half of a member's XP the tab could not see. `getStaked` reads
 * `getUserStakes` + `stakes()`, and both of those describe a lot as it stands
 * NOW: a closed lot comes back with `status = 1` and no record of WHEN it
 * closed. Without the exit time there is no served duration, and without a
 * served duration there is no XP figure — which is why supernovaXpBalance
 * carried a null `BANKED_BASE_XP` stub and the tile called itself "XP accrued".
 *
 * THE LOG CARRIES WHAT STORAGE DOESN'T. Three events, all verified against
 * every one of this contract's 572 historical logs (SUPERNOVA_ABI_FACTS.md —
 * "572 decoded, 0 failures"), close that gap:
 *
 *   TokenStaked              stakeId, user, token, tokenId, amount, duration,
 *                            unlockTime, status  — the whole lot, at open time.
 *                            startTime is unlockTime − duration, a relationship
 *                            confirmed exactly on a real stake in that document.
 *   TokenUnstaked            stakeId, user, token, tokenId, amount — the exit.
 *   TokenEmergencyUnstaked   structurally IDENTICAL to TokenUnstaked. The two
 *                            are distinguishable only by topic0, which is why
 *                            they are read as two separate queries here rather
 *                            than one merged filter.
 *
 * The exit's BLOCK TIMESTAMP is the exit time. supernovaChain fetches it; this
 * module is handed it.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT DECIDE. It does not classify an exit as
 * matured or early from which function was called. `status` is 1 after BOTH
 * exits (facts doc, open item 1) and the contract's revert conditions were never
 * tested, so the only trustworthy test is arithmetic: exit time against the
 * lot's own `unlockTime`. That comparison lives in config/supernovaXp
 * (`isMatured`), reached through supernovaExitQuote, so the banked half is rated
 * by exactly the same model as the accruing half. Nothing re-implements it here.
 *
 * EXCLUDED IS NOT ZERO, AND IS NEVER SILENT. A closed lot we cannot rebuild
 * confidently — no opening log, an unreadable field, a missing block timestamp,
 * an exit that predates its own stake — is dropped from the total AND COUNTED.
 * The count reaches the XP tile as a footnote. Guessing at one of these would
 * put a fabricated lifetime figure in front of a member, which is the exact
 * failure the whole data layer exists to prevent.
 *
 * PURE. No viem, no React, no network: it takes decoded log arguments (viem's
 * `getLogs({ event })` shape) and block timestamps, and returns lots.
 */

/* BigInt is ES2020 and every browser in our browserslist has it; the CRA eslint
   env doesn't declare it (same note as supernovaChain.js). */
/* global BigInt */

/**
 * The three fragments, human-readable so `parseAbiItem` takes them directly and
 * this module stays dependency-free. Parameter order and `indexed` placement are
 * copied from SUPERNOVA_ABI_FACTS.md verbatim — note `token` sits BETWEEN the
 * indexed `user` and the indexed `tokenId`, which is the detail that silently
 * mis-decodes if it is "tidied up".
 */
export const TOKEN_STAKED_EVENT =
  "event TokenStaked(uint256 indexed stakeId, address indexed user, address token, " +
  "uint256 indexed tokenId, uint256 amount, uint256 duration, uint256 unlockTime, uint8 status)";

export const TOKEN_UNSTAKED_EVENT =
  "event TokenUnstaked(uint256 indexed stakeId, address indexed user, address token, " +
  "uint256 indexed tokenId, uint256 amount)";

export const TOKEN_EMERGENCY_UNSTAKED_EVENT =
  "event TokenEmergencyUnstaked(uint256 indexed stakeId, address indexed user, address token, " +
  "uint256 indexed tokenId, uint256 amount)";

/** Seconds → ms, for the contract's second-resolution timestamps. */
const MS_PER_SECOND = 1000;

const sameAddress = (a, b) =>
  typeof a === "string" &&
  typeof b === "string" &&
  a.toLowerCase() === b.toLowerCase();

/** bigint | number | decimal string → number. null when it isn't one of those. */
function toNumber(value) {
  if (typeof value === "bigint") {
    return value >= 0 && value <= BigInt(Number.MAX_SAFE_INTEGER)
      ? Number(value)
      : null;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && /^\d+$/.test(value)) {
    const n = Number(value);
    return Number.isSafeInteger(n) ? n : null;
  }
  return null;
}

/** The stake id as a string, which is how both log sets are keyed together. */
export function stakeIdKey(value) {
  if (typeof value === "bigint") return String(value);
  const n = toNumber(value);
  return n === null ? null : String(n);
}

/** One log's decoded arguments, whatever shape the client hands them in. */
const argsOf = (log) => (log && typeof log === "object" ? log.args || log : null);

/**
 * A `TokenStaked` log → the lot it opened.
 *
 * @returns {Object|null} null ⇒ not a lot this tab shows (another whitelisted
 *          collection, or an id outside the four Supernovas). Anything that IS
 *          ours but unreadable comes back with `readable: false` so the caller
 *          can count it rather than drop it.
 */
export function decodeOpenedLog(log) {
  const args = argsOf(log);
  if (!args) return null;

  const stakeId = stakeIdKey(args.stakeId);
  if (stakeId === null) return null;

  /* This contract stakes four whitelisted collections. A member's stake in
     another one is real; it is simply not this tab's business, and leaving it
     out is not the same as failing to read it. */
  if (!sameAddress(args.token, SUPERNOVA_COLLECTION_ADDRESS)) return null;

  const tokenId = toNumber(args.tokenId);
  if (tokenId === null || !SUPERNOVA_TOKEN_IDS.includes(tokenId)) return null;

  const qty = toNumber(args.amount);
  const lockDays = lockDaysFromSeconds(args.duration);
  const unlockSeconds = toNumber(args.unlockTime);
  const durationSeconds = toNumber(args.duration);

  if (
    qty === null ||
    qty <= 0 ||
    !Number.isInteger(qty) ||
    lockDays === null ||
    unlockSeconds === null ||
    durationSeconds === null
  ) {
    return { stakeId, tokenId, readable: false };
  }

  /* startTime is not in the log, but unlockTime − duration is: verified exactly
     on a real stake (1765659810 + 86400 = 1765746210) in the facts doc. */
  const stakedAtMs = (unlockSeconds - durationSeconds) * MS_PER_SECOND;
  if (!Number.isFinite(stakedAtMs) || stakedAtMs <= 0) {
    return { stakeId, tokenId, readable: false };
  }

  return {
    stakeId,
    readable: true,
    user: typeof args.user === "string" ? args.user : null,
    lotId: lotIdFor(stakeId),
    tokenId,
    qty,
    lockDays,
    stakedAtMs,
    unlockAtMs: unlockSeconds * MS_PER_SECOND,
  };
}

/**
 * A `TokenUnstaked` / `TokenEmergencyUnstaked` log → the exit it recorded.
 *
 * `emergency` is carried for the record only. It does NOT decide how the lot is
 * rated — see the module comment.
 *
 * @returns {Object|null} null ⇒ another whitelisted collection's exit, which is
 *          FILTERED here rather than left to fail the opening-log pairing later.
 *          Getting that wrong turns "not this tab's stake" into "a closed stake
 *          we couldn't read", and puts a footnote on the member's XP tile about
 *          a lot that was never missing. An exit whose `token` we cannot read at
 *          all is kept, so the pairing decides — an unreadable field must not
 *          become a silent drop.
 */
export function decodeExitLog(log, { emergency = false } = {}) {
  const args = argsOf(log);
  if (!args) return null;
  const stakeId = stakeIdKey(args.stakeId);
  if (stakeId === null) return null;
  if (
    typeof args.token === "string" &&
    !sameAddress(args.token, SUPERNOVA_COLLECTION_ADDRESS)
  ) {
    return null;
  }
  const blockNumber = log && log.blockNumber !== undefined ? log.blockNumber : null;
  return {
    stakeId,
    emergency: Boolean(emergency),
    /* A string, because block numbers arrive as bigints and this is a map key. */
    blockKey: blockNumber === null ? null : String(blockNumber),
  };
}

/**
 * @typedef {Object} ClosedLotRecord
 * @property {string} lotId
 * @property {string} stakeId
 * @property {number} tokenId
 * @property {number} qty
 * @property {number} lockDays
 * @property {number} stakedAtMs
 * @property {number} unlockAtMs
 * @property {number} exitAtMs     block timestamp of the exit, in ms
 * @property {boolean} emergency   which exit the member called — record only
 */

/**
 * Pair the opening logs with the exits and produce the closed lots.
 *
 * @param {Object} input
 * @param {Array} input.opened        TokenStaked logs for this wallet
 * @param {Array} input.exits         decodeExitLog() results, both kinds
 * @param {Object} input.blockTimesMs blockNumber (as a string) → epoch ms
 * @param {string} [input.wallet]     the address the logs were filtered by
 * @returns {{lots: ClosedLotRecord[], excluded: number}}
 */
export function buildClosedLots(input = {}) {
  const { opened = [], exits = [], blockTimesMs = {}, wallet = null } = input;

  const openedById = new Map();
  opened.forEach((log) => {
    const lot = decodeOpenedLog(log);
    /* null ⇒ another collection, which is filtered rather than excluded. A
       first decode wins: stake ids are issued by a counter and never reused. */
    if (lot && !openedById.has(lot.stakeId)) openedById.set(lot.stakeId, lot);
  });

  /* Earliest exit per stake id. A lot closes once; a duplicate would mean we
     read the same log twice, and taking the earliest keeps that idempotent. */
  const exitById = new Map();
  exits.forEach((exit) => {
    if (!exit || exit.stakeId === null || exit.stakeId === undefined) return;
    const seen = exitById.get(exit.stakeId);
    const block = Number(exit.blockKey);
    if (!seen || (Number.isFinite(block) && block < Number(seen.blockKey))) {
      exitById.set(exit.stakeId, exit);
    }
  });

  const lots = [];
  let excluded = 0;

  exitById.forEach((exit, stakeId) => {
    const lot = openedById.get(stakeId);
    /* An exit whose opening log we never saw is a lot we cannot describe at
       all — no quantity, no lock, no start. It is not another collection's
       (those never reach here with a matching id), so it is EXCLUDED. */
    if (!lot) {
      excluded += 1;
      return;
    }
    if (!lot.readable) {
      excluded += 1;
      return;
    }
    /* getLogs was filtered on the `user` topic, so a mismatch means our topic
       or field mapping slipped. We can't trust any field in that record. */
    if (wallet && lot.user && !sameAddress(lot.user, wallet)) {
      excluded += 1;
      return;
    }

    const exitAtMs = blockTimesMs ? blockTimesMs[exit.blockKey] : undefined;
    if (typeof exitAtMs !== "number" || !Number.isFinite(exitAtMs) || exitAtMs <= 0) {
      excluded += 1;
      return;
    }
    /* An exit that predates its own stake is not a lot with a short duration —
       it is a reading we got wrong somewhere, and rating it would invent XP. */
    if (exitAtMs < lot.stakedAtMs) {
      excluded += 1;
      return;
    }

    lots.push({
      lotId: lot.lotId,
      stakeId: lot.stakeId,
      tokenId: lot.tokenId,
      qty: lot.qty,
      lockDays: lot.lockDays,
      stakedAtMs: lot.stakedAtMs,
      unlockAtMs: lot.unlockAtMs,
      exitAtMs,
      emergency: exit.emergency,
    });
  });

  /* Most recently closed first — the order a member remembers them in. */
  lots.sort((a, b) => b.exitAtMs - a.exitAtMs);

  return { lots, excluded };
}

/** Every block a set of exits needs a timestamp for, deduplicated. */
export function exitBlockKeys(exits = []) {
  const keys = [];
  const seen = new Set();
  exits.forEach((exit) => {
    if (!exit || !exit.blockKey || seen.has(exit.blockKey)) return;
    seen.add(exit.blockKey);
    keys.push(exit.blockKey);
  });
  return keys;
}

export default buildClosedLots;
